import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';
import { EmployeeImportResponse } from '../../employee.model';

export type EmployeeImportsPageChangedEvent = {
  pageNumber: number;
  pageSize: number;
};

@Component({
  selector: 'employee-imports-table',
  imports: [DatePipe, ...SharedMaterialUIModule],
  templateUrl: './employee-imports-table.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class EmployeeImportsTableComponent {
  readonly imports = input<EmployeeImportResponse[]>([]);
  readonly loading = input(false);
  readonly pageNumber = input(1);
  readonly pageSize = input(25);
  readonly totalCount = input(0);
  readonly totalPages = input(0);
  readonly downloadingImportId = input<string | null>(null);
  readonly downloadingErrorImportId = input<string | null>(null);
  readonly downloadingSummaryImportId = input<string | null>(null);

  readonly refreshed = output<void>();
  readonly pageChanged = output<EmployeeImportsPageChangedEvent>();
  readonly downloadRequested = output<EmployeeImportResponse>();
  readonly downloadErrorsRequested = output<EmployeeImportResponse>();
  readonly downloadSummaryRequested = output<EmployeeImportResponse>();

  protected canGoToPreviousPage(): boolean {
    return this.pageNumber() > 1;
  }

  protected canGoToNextPage(): boolean {
    return this.totalPages() > 0 && this.pageNumber() < this.totalPages();
  }

  protected previousPage(): void {
    if (!this.canGoToPreviousPage()) {
      return;
    }

    this.pageChanged.emit({
      pageNumber: this.pageNumber() - 1,
      pageSize: this.pageSize(),
    });
  }

  protected nextPage(): void {
    if (!this.canGoToNextPage()) {
      return;
    }

    this.pageChanged.emit({
      pageNumber: this.pageNumber() + 1,
      pageSize: this.pageSize(),
    });
  }

  protected download(item: EmployeeImportResponse): void {
    this.downloadRequested.emit(item);
  }

  protected downloadErrors(item: EmployeeImportResponse): void {
    this.downloadErrorsRequested.emit(item);
  }

  protected downloadSummary(item: EmployeeImportResponse): void {
    this.downloadSummaryRequested.emit(item);
  }

  protected formatFileSize(bytes: number): string {
    if (bytes < 1024) {
      return `${bytes} B`;
    }

    const units = ['KB', 'MB', 'GB', 'TB'];
    let value = bytes;
    let unitIndex = -1;

    do {
      value /= 1024;
      unitIndex += 1;
    } while (value >= 1024 && unitIndex < units.length - 1);

    return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unitIndex]}`;
  }

  protected isDownloading(importId: string): boolean {
    return this.downloadingImportId() === importId;
  }

  protected isErrorDownloading(importId: string): boolean {
    return this.downloadingErrorImportId() === importId;
  }

  protected isSummaryDownloading(importId: string): boolean {
    return this.downloadingSummaryImportId() === importId;
  }

  protected canDownloadSummary(item: EmployeeImportResponse): boolean {
    if (item.completedDate) {
      return true;
    }

    const normalizedStatus = item.status.toLowerCase();
    return (
      normalizedStatus.includes('completed') ||
      normalizedStatus.includes('succeeded') ||
      normalizedStatus.includes('success') ||
      normalizedStatus.includes('failed') ||
      normalizedStatus.includes('error')
    );
  }

  protected statusClass(status: string): string {
    const normalizedStatus = status.toLowerCase();

    if (normalizedStatus.includes('failed') || normalizedStatus.includes('error')) {
      return 'bg-rose-100 text-rose-800 border-rose-200';
    }

    if (normalizedStatus.includes('completed') || normalizedStatus.includes('success')) {
      return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    }

    if (normalizedStatus.includes('queued') || normalizedStatus.includes('processing') || normalizedStatus.includes('running')) {
      return 'bg-amber-100 text-amber-800 border-amber-200';
    }

    return 'bg-slate-100 text-slate-700 border-slate-200';
  }
}
